'use client'

import { useState, useEffect } from 'react'

// Simple analytics helper
function track(eventName: string, data?: Record<string, any>) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    ;(window as any).gtag('event', eventName, data)
  }
}

// Get UTM parameters from URL
function getUTMParams(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  
  const params = new URLSearchParams(window.location.search)
  const utmParams: Record<string, string> = {}
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
  utmKeys.forEach(key => {
    const value = params.get(key)
    if (value) {
      utmParams[key] = value
    }
  })
  
  return utmParams
}

export default function EarlyAccessForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    programType: '',
    entriesPerCycle: '',
    notes: '',
    website: '', // Honeypot field
  })
  const [utmParams, setUtmParams] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  // Capture UTM parameters on mount
  useEffect(() => {
    setUtmParams(getUTMParams())
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Reset errors
    setErrors({})

    // Validation
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required'
    }
    
    if (!formData.programType) {
      newErrors.programType = 'Program type is required'
    }
    
    if (!formData.entriesPerCycle) {
      newErrors.entriesPerCycle = 'Entries per cycle is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)

    try {
      // Get referrer and user agent
      const referrer = typeof document !== 'undefined' ? document.referrer : ''
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''

      const response = await fetch('/api/awardplex', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          programType: formData.programType,
          entriesPerCycle: formData.entriesPerCycle,
          notes: formData.notes,
          source: 'awardplex',
          timestamp: new Date().toISOString(),
          pagePath: '/awardplex',
          referrer: referrer || undefined,
          userAgent: userAgent || undefined,
          ...utmParams,
          // Honeypot - if filled, bot detected
          website: formData.website,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit form')
      }

      // Success
      setSubmitSuccess(true)
      track('awardplex_early_access_submit', {
        program_type: formData.programType,
        entries_per_cycle: formData.entriesPerCycle,
      })
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        programType: '',
        entriesPerCycle: '',
        notes: '',
        website: '',
      })
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to submit. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <div className="p-8 rounded-[20px] border border-blue-500/30 bg-blue-500/10 text-center">
        <p className="text-lg text-blue-300">Thanks — we'll get back to you shortly.</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium mb-2">
          Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
          placeholder="Your name"
          aria-invalid={errors.name ? 'true' : 'false'}
          aria-describedby={errors.name ? 'name-error' : undefined}
        />
        {errors.name && (
          <p id="name-error" className="mt-1 text-sm text-red-400" role="alert">
            {errors.name}
          </p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-2">
          Work email <span className="text-red-400">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
          placeholder="you@company.com"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-400" role="alert">
            {errors.email}
          </p>
        )}
      </div>

      {/* Company */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium mb-2">
          Company <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
          placeholder="Your company"
          aria-invalid={errors.company ? 'true' : 'false'}
          aria-describedby={errors.company ? 'company-error' : undefined}
        />
        {errors.company && (
          <p id="company-error" className="mt-1 text-sm text-red-400" role="alert">
            {errors.company}
          </p>
        )}
      </div>

      {/* Program Type */}
      <div>
        <label htmlFor="programType" className="block text-sm font-medium mb-2">
          Program type <span className="text-red-400">*</span>
        </label>
        <select
          id="programType"
          name="programType"
          value={formData.programType}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
          aria-invalid={errors.programType ? 'true' : 'false'}
          aria-describedby={errors.programType ? 'programType-error' : undefined}
        >
          <option value="">Select program type</option>
          <option value="Internal awards">Internal awards</option>
          <option value="Partner/Vendor awards">Partner/Vendor awards</option>
          <option value="Employee recognition">Employee recognition</option>
          <option value="Industry awards">Industry awards</option>
          <option value="Other">Other</option>
        </select>
        {errors.programType && (
          <p id="programType-error" className="mt-1 text-sm text-red-400" role="alert">
            {errors.programType}
          </p>
        )}
      </div>

      {/* Entries Per Cycle */}
      <div>
        <label htmlFor="entriesPerCycle" className="block text-sm font-medium mb-2">
          Entries per cycle <span className="text-red-400">*</span>
        </label>
        <select
          id="entriesPerCycle"
          name="entriesPerCycle"
          value={formData.entriesPerCycle}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
          aria-invalid={errors.entriesPerCycle ? 'true' : 'false'}
          aria-describedby={errors.entriesPerCycle ? 'entriesPerCycle-error' : undefined}
        >
          <option value="">Select range</option>
          <option value="<50">&lt;50</option>
          <option value="50–200">50–200</option>
          <option value="200–1000">200–1000</option>
          <option value="1000+">1000+</option>
        </select>
        {errors.entriesPerCycle && (
          <p id="entriesPerCycle-error" className="mt-1 text-sm text-red-400" role="alert">
            {errors.entriesPerCycle}
          </p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium mb-2">
          Notes <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={4}
          className="w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all resize-none"
          placeholder="Tell us more about your awards program..."
        />
      </div>

      {/* Honeypot field - hidden from users */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="p-4 rounded-[12px] border border-red-500/30 bg-red-500/10">
          <p className="text-sm text-red-400" role="alert">
            {errors.submit}
          </p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full px-8 py-4 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white rounded-[16px] font-medium transition-colors text-base shadow-lg shadow-blue-500/20"
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </form>
  )
}

